import { clientConfig } from '@/config/clientConfig';
import { useToast } from '@/shared/hooks/useToast';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { CheckCircle2, Circle, Eye, EyeOff, Loader2, Lock } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuthStore } from '../store/authStore';

export default function ChangePasswordPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { pendingChallenge, submitNewPassword, isLoading, error, clearError } = useAuthStore();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (!pendingChallenge) {
      navigate('/login', { replace: true });
    }
  }, [pendingChallenge, navigate]);

  useEffect(() => {
    return () => {
      clearError();
    };
  }, [clearError]);

  const validity = useMemo(
    () => ({
      hasLength: newPassword.length >= 12,
      hasUpper: /[A-Z]/.test(newPassword),
      hasLower: /[a-z]/.test(newPassword),
      hasSpecial: /[!@#$%^&*_+\-=|<>/?~]/.test(newPassword),
      passwordMatch: confirmPassword.length > 0 ? newPassword === confirmPassword : true,
    }),
    [newPassword, confirmPassword],
  );

  const allValid =
    validity.hasLength &&
    validity.hasUpper &&
    validity.hasLower &&
    validity.hasSpecial &&
    validity.passwordMatch &&
    confirmPassword.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!allValid) return;

    try {
      await submitNewPassword(newPassword);
      toast({
        title: 'Password Changed',
        description: 'Your password has been set. Please log in with your new password.',
        className:
          'bg-green-50 border-green-200 text-green-900 dark:bg-green-900 dark:text-green-100',
      });
      navigate('/login', { replace: true });
    } catch (_err) {
      toast({
        title: 'Failed to Set Password',
        description: error || 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  if (!pendingChallenge) return null;

  const requirements = [
    { key: 'hasLength', label: '12 or more characters', met: validity.hasLength },
    { key: 'hasUpper', label: 'At least 1 uppercase letter', met: validity.hasUpper },
    { key: 'hasLower', label: 'At least 1 lowercase letter', met: validity.hasLower },
    { key: 'hasSpecial', label: 'At least 1 special character', met: validity.hasSpecial },
  ];

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={{
        backgroundImage: `url(${clientConfig.loginBackground})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/40 to-green-900/40" />

      <Card className="w-full max-w-md p-8 space-y-6 bg-white dark:bg-gray-900 shadow-2xl border-border relative z-10">
        <div className="text-center space-y-2">
          <div className="flex justify-center mb-4">
            <img src={clientConfig.logo} alt={clientConfig.name} className="h-12 w-auto" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Set Your New Password
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Your account requires a password change before you can continue.
          </p>
        </div>

        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2">
          <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
            Password requirements:
          </p>
          {requirements.map((req) => (
            <div key={req.key} className="flex items-center gap-2 text-sm">
              {req.met ? (
                <CheckCircle2 className="w-4 h-4 text-green-600" />
              ) : (
                <Circle className="w-4 h-4 text-gray-400" />
              )}
              <span
                className={
                  req.met
                    ? 'text-green-700 dark:text-green-400'
                    : 'text-gray-600 dark:text-gray-400'
                }
              >
                {req.label}
              </span>
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label
              htmlFor="newPassword"
              className="text-sm font-medium text-gray-900 dark:text-gray-100"
            >
              New Password
            </Label>
            <div className="relative">
              <Input
                id="newPassword"
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                disabled={isLoading}
                className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 pr-10"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="confirmPassword"
              className="text-sm font-medium text-gray-900 dark:text-gray-100"
            >
              Confirm Password
            </Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                disabled={isLoading}
                className={`bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 pr-10 ${!validity.passwordMatch ? 'border-red-500' : ''}`}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                tabIndex={-1}
              >
                {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {!validity.passwordMatch && (
              <p className="text-xs text-red-500">Passwords do not match</p>
            )}
          </div>

          <Button
            type="submit"
            disabled={isLoading || !allValid}
            className="w-full text-white"
            style={{ backgroundColor: clientConfig.theme.primary }}
            size="lg"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Setting password...
              </>
            ) : (
              <>
                <Lock className="w-4 h-4 mr-2" />
                Set New Password
              </>
            )}
          </Button>
        </form>
      </Card>
    </div>
  );
}
